/**
 * Admin -- diagnostics + ops smoke tests.
 *
 * One-page console for verifying that the operational wiring (Sentry, on-call
 * paging, critical health probes, LLM spend cap) is live without staging an
 * actual incident. Each button records to the audit chain so a follow-up
 * inspection of /audit confirms the path fired end-to-end.
 *
 * ADMIN-only -- non-admins see the RoleGateCard.
 */

import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Activity, Bell, Bug, DollarSign, Heart, ShieldCheck, AlertTriangle } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/stores/auth';
import { RoleGateCard } from './AdminPromptVersions';

interface SentryTestResp {
  ok: boolean;
  sentryConfigured: boolean;
  eventId: string | null;
  note: string;
}

interface OncallTestResp {
  paged: boolean;
  reason?: string;
}

interface SpendCapResp {
  status: { status: 'ok' | 'soft-cap-exceeded' | 'hard-cap-exceeded'; spendUSD: number; capUSD: number; utilisation: number; reason?: string };
  month: { spendUSD: number; callCount: number; tokensIn: number; tokensOut: number };
}

interface HealthCriticalResp {
  ok: boolean;
  checks: Record<string, { ok: boolean; latencyMs?: number; detail?: string }>;
}

export function AdminDiagnosticsPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'ADMIN';

  if (!isAdmin) {
    return <RoleGateCard requiredRole="ADMIN" surface="Diagnostics" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Diagnostics</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Smoke-test the operational wiring without staging a real incident. Every test fires through the
          live transport and records to the audit chain.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <CriticalHealthCard />
        <SpendCapCard />
        <SentryTestCard />
        <OncallTestCard />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">What to expect</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-xs text-muted-foreground">
          <p><strong className="text-foreground">Critical health</strong> -- live JSON from /health/critical with db, audit and migration probes.</p>
          <p><strong className="text-foreground">Spend cap</strong> -- this tenant's current LLM spend vs cap. The router refuses calls past hard cap with HTTP 402.</p>
          <p><strong className="text-foreground">Sentry test</strong> -- fires <code>captureMessage()</code>; appears in the Sentry inbox within ~30s if SENTRY_DSN is set.</p>
          <p><strong className="text-foreground">On-call test</strong> -- fires an INFO Slack page (no oncall ping). Dedupe window 5 min so spamming the button is safe.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function CriticalHealthCard() {
  // /health/critical lives at /health/critical -- API root not /api root.
  const q = useQuery({
    queryKey: ['admin', 'diagnostics', 'critical'],
    queryFn: async () => (await api.get<HealthCriticalResp>('/health/critical')).data,
    refetchInterval: 30_000,
  });

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Heart className="h-4 w-4 text-primary" /> Critical health
          </CardTitle>
          <CardDescription>Refreshes every 30 seconds.</CardDescription>
        </div>
        {q.data && (
          <Badge variant={q.data.ok ? 'success' : 'destructive'}>
            {q.data.ok ? 'all green' : 'degraded'}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {q.isLoading && <p className="text-xs text-muted-foreground">Probing...</p>}
        {q.data && (
          <ul className="space-y-1 text-xs">
            {Object.entries(q.data.checks).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-1.5">
                <span className="flex items-center gap-2">
                  <span className={`inline-block h-2 w-2 rounded-full ${v.ok ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  <span className="font-mono">{k}</span>
                </span>
                <span className="text-muted-foreground">
                  {v.latencyMs != null ? `${v.latencyMs}ms` : ''}
                  {v.detail && ` -- ${v.detail}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function SpendCapCard() {
  const q = useQuery({
    queryKey: ['admin', 'diagnostics', 'spend-cap'],
    queryFn: async () => (await api.get<SpendCapResp>('/api/admin/diagnostics/spend-cap')).data,
    refetchInterval: 60_000,
  });

  const pct = q.data ? Math.min(100, Math.round(q.data.status.utilisation * 100)) : 0;
  const colour = !q.data
    ? 'bg-gray-300'
    : q.data.status.status === 'hard-cap-exceeded'
    ? 'bg-destructive'
    : q.data.status.status === 'soft-cap-exceeded'
    ? 'bg-amber-500'
    : 'bg-emerald-500';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-primary" /> LLM spend cap
        </CardTitle>
        <CardDescription>This tenant's MTD spend vs configured cap.</CardDescription>
      </CardHeader>
      <CardContent>
        {q.isLoading && <p className="text-xs text-muted-foreground">Loading...</p>}
        {q.data && (
          <div className="space-y-2">
            <div className="flex items-baseline justify-between text-xs">
              <span className="font-display text-base">${q.data.status.spendUSD.toFixed(2)}</span>
              <span className="text-muted-foreground">of ${q.data.status.capUSD.toFixed(0)} ({pct}%)</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div className={`h-full ${colour}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
              <span>{q.data.month.callCount.toLocaleString()} calls MTD</span>
              <span>{(q.data.month.tokensIn + q.data.month.tokensOut).toLocaleString()} tokens MTD</span>
              <Badge variant={q.data.status.status === 'ok' ? 'success' : q.data.status.status === 'soft-cap-exceeded' ? 'warning' : 'destructive'}>
                {q.data.status.status.replace(/-/g, ' ')}
              </Badge>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SentryTestCard() {
  const [last, setLast] = useState<SentryTestResp | null>(null);
  const fire = useMutation({
    mutationFn: async () => (await api.post<SentryTestResp>('/api/admin/diagnostics/sentry-test')).data,
    onSuccess: (data) => {
      setLast(data);
      toast.success('Sentry test sent', data.note);
    },
    onError: (err) => toast.error('Sentry test failed', extractApiError(err).message),
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Bug className="h-4 w-4 text-primary" /> Sentry capture
        </CardTitle>
        <CardDescription>Fire a captureMessage and confirm Sentry receives it.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button onClick={() => fire.mutate()} disabled={fire.isPending}>
          <Activity className="mr-1.5 h-3.5 w-3.5" /> {fire.isPending ? 'Sending...' : 'Fire test event'}
        </Button>
        {last && (
          <div className="rounded-md border bg-muted/40 p-2 text-xs">
            <div className="flex items-center gap-2">
              {last.sentryConfigured ? (
                <ShieldCheck className="h-3 w-3 text-emerald-600" />
              ) : (
                <AlertTriangle className="h-3 w-3 text-amber-600" />
              )}
              <strong>{last.sentryConfigured ? 'Captured' : 'Log-only'}</strong>
              {last.eventId && <code className="font-mono text-[10px]">{last.eventId}</code>}
            </div>
            <p className="mt-1 text-muted-foreground">{last.note}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OncallTestCard() {
  const [last, setLast] = useState<OncallTestResp | null>(null);
  const fire = useMutation({
    mutationFn: async () => (await api.post<OncallTestResp>('/api/admin/diagnostics/oncall-test')).data,
    onSuccess: (data) => {
      setLast(data);
      toast.success(data.paged ? 'Slack page sent' : 'Logged only', data.reason ?? '');
    },
    onError: (err) => toast.error('On-call test failed', extractApiError(err).message),
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary" /> On-call paging
        </CardTitle>
        <CardDescription>Fires an INFO Slack page (no actual oncall ping). 5-min dedupe.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button onClick={() => fire.mutate()} disabled={fire.isPending}>
          <Bell className="mr-1.5 h-3.5 w-3.5" /> {fire.isPending ? 'Paging...' : 'Fire test page'}
        </Button>
        {last && (
          <div className="rounded-md border bg-muted/40 p-2 text-xs">
            <div className="flex items-center gap-2">
              {last.paged ? (
                <ShieldCheck className="h-3 w-3 text-emerald-600" />
              ) : (
                <AlertTriangle className="h-3 w-3 text-amber-600" />
              )}
              <strong>{last.paged ? 'Paged' : 'Not paged'}</strong>
              {last.reason && <code className="font-mono text-[10px]">{last.reason}</code>}
            </div>
            <p className="mt-1 text-muted-foreground">
              {last.paged
                ? 'Check the on-call Slack channel for the [INFO] test page.'
                : 'No webhook configured -- log-only path verified.'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
